import { Analytics } from '@cobliteam/analytics';
import { useUnresolvedIncidents } from '@cobliteam/hooks/src/useEntity/useStatusPage';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import {
  Button,
  Divider,
  Popover,
  Tag,
  Timeline,
  TimelineContent,
  TimelineDescription,
  TimelineHeader,
  TimelineIcon,
  TimelineItem,
  TimelineTime,
  TimelineTitle,
  Tooltip,
  Typography,
} from '@cobliteam/ui/components';
import Card, { CardContent, CardHeader, CardTitle } from '@cobliteam/ui/components/Card';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';

const Incidents = () => {
  const { t } = useTranslation('app-header');
  const featureFlag = useFeatureFlags();
  const {
    data: incidentsData,
    isLoading,
    error,
  } = useUnresolvedIncidents({
    enabled: featureFlag.sidebarIncidents,
  });

  if (!featureFlag.sidebarIncidents) return null;

  const hasIncidents = incidentsData?.incidents && incidentsData.incidents.length > 0;
  if (!hasIncidents) return null;

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical':
        return 'danger';
      case 'major':
        return 'warning';
      case 'minor':
        return 'attention';
      default:
        return 'default';
    }
  };

  const getImpactLabel = (impact: string) => {
    switch (impact) {
      case 'critical':
        return t('incidents.critical');
      case 'major':
        return t('incidents.major');
      case 'minor':
        return t('incidents.minor');
      default:
        return t('incidents.unknown');
    }
  };

  // Investigating, Identified, Monitoring, Resolved, or Postmortem
  const getUpdateType = (update: any) => {
    switch (update.status) {
      case 'investigating':
        return t('incidents.investigating');
      case 'identified':
        return t('incidents.identified');
      case 'monitoring':
        return t('incidents.monitoring');
    }
    return t('incidents.unknown');
  };

  const getUpdateColor = (update: any) => {
    switch (update.status) {
      case 'investigating':
        return 'default';
      case 'identified':
        return 'warning';
      case 'monitoring':
        return 'primary';
    }

    return 'primary';
  };

  const getUpdateIcon = (update: any) => {
    switch (update.status) {
      case 'investigating':
        return 'search';
      case 'identified':
        return 'triangle-alert';
      case 'monitoring':
        return 'activity';
    }

    return 'triangle-alert';
  };

  return (
    <Popover>
      <Tooltip content={t('incidents.tooltip')} position='right' asChild>
        <PopoverTrigger asChild>
          <div className='relative p-1.5'>
            <Button
              data-testid='app-header-incidents-button'
              variant='surface'
              color='warning'
              size='sm'
              icon={<Icon name='triangle-alert' />}
              onClick={() => Analytics.track('NavBar - Incidents - Click')}
            />
            {incidentsData?.incidents && incidentsData.incidents.length > 0 && (
              <div className='absolute top-0 right-0 flex size-5 items-center justify-center rounded-full bg-danger font-medium text-white text-xs'>
                {incidentsData.incidents.length}
              </div>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align='end'
        side='right'
        showArrow={false}
        className='scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-9 flex max-h-[75vh] w-fit max-w-lg flex-col gap-4 overflow-auto border-gray-6 shadow-xl'
      >
        <div className='flex flex-col gap-3'>
          {/* Incidents List */}
          {isLoading && (
            <div className='flex items-center justify-center py-4'>
              <Icon name='cb-loading-spin' className='mr-2 animate-spin' />
              <Typography variant='text-sm'>{t('incidents.loading')}</Typography>
            </div>
          )}

          {!(isLoading || error) && incidentsData?.incidents && incidentsData.incidents.length > 0 && (
            <div className='space-y-3'>
              {incidentsData.incidents.map((incident) => (
                <Card key={incident.id}>
                  <CardHeader className='pb-2' divider>
                    <div className='flex items-start justify-between gap-4'>
                      <div>
                        <CardTitle className='flex items-center gap-1 font-semibold text-md'>
                          <Typography variant='text-lg-semibold'>{incident.name}</Typography>
                        </CardTitle>
                      </div>
                      {incident.impact !== 'none' && (
                        <div className='flex gap-1'>
                          <Tag label={getImpactLabel(incident.impact)} icon={<Icon name='triangle-alert' />} variant='surface' color={getImpactColor(incident.impact)} size='sm' />
                        </div>
                      )}
                    </div>
                    <div className='text-gray-11 text-xs'>
                      {t('incidents.start')}{' '}
                      {new Date(incident.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {incident.incident_updates?.length > 0 && (
                      <Timeline>
                        {incident.incident_updates.map((update) => (
                          <TimelineItem key={update.id} color={getUpdateColor(update)}>
                            <TimelineIcon color={getUpdateColor(update)} icon={getUpdateIcon(update)} />
                            <TimelineContent>
                              <TimelineHeader>
                                <TimelineTitle>{getUpdateType(update)}</TimelineTitle>
                                <TimelineTime>
                                  {new Date(update.updated_at).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </TimelineTime>
                              </TimelineHeader>
                              <TimelineDescription>{update.body}</TimelineDescription>
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    )}

                    {incident.components && incident.components.length > 0 && (
                      <>
                        <Divider className='my-3' />
                        <div className='mb-3 flex flex-col gap-1'>
                          <Typography variant='text-md-medium'>{t('incidents.possible_affected_areas')}</Typography>
                          <div className='mt-1 flex flex-wrap gap-1'>
                            {incident.components.map((component) => (
                              <Tag key={component.id} label={component.name} variant='surface' size='sm' />
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <div className='mt-2 ml-auto flex items-center justify-between border-gray-6 border-t pt-2'>
                      <Button
                        variant='ghost'
                        size='sm'
                        endIcon={<Icon name='arrow-up-right' />}
                        onClick={() => {
                          window.open(incident.shortlink);
                          Analytics.track('NavBar - IncidentDetails - Click', { incidentId: incident.id });
                        }}
                        type='button'
                      >
                        {t('incidents.viewIncident')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default Incidents;
